import * as React from "react";
import {
  Redirect,
  Route as R,
  RouteComponentProps
} from "react-router-dom";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import * as H from "history";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import Grid from "src/library/components/Hedron";
import { MenuType } from "src/utils/menu/menus";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import {
  MenuContext,
  ContextType
} from "src/core/shared/hooks/menu/useMenu";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

import GenericError from "../Public/GenericError";
import NoAccess from "../Public/NoAccess";
import NotFound from "../Public/NotFound";
import {
  ILocationState,
  IRoute
} from "./routes";

export interface IRouteProps extends IRoute {
  pageComponent?: React.ReactFCWithChildren<any>;
  redirect?: string;
  privateRoute?: boolean;
  path?: string;
  menuType: MenuType;
  title: string | null;
  allowedContexts?: ContextType[];
  authenticated?: boolean;
  hasPermission?: boolean;
  terminology: ITerminology;
}

const Route = React.memo((routeProps: IRouteProps) => {
  const {
    pageComponent: PageComponent,
    title,
    redirect,
    privateRoute,
    authenticated,
    hasPermission,
    menuType,
    location,
    terminology,
    ...rest
  } = routeProps;

  const [ , { handleRouteLoad } ] = React.useContext(MenuContext);
  const [ _settings, { checkGlobalSettingsAreSynced } ] = useGlobalSettings();

  React.useEffect(() => {
    // Run synchronisers
    checkGlobalSettingsAreSynced();

    if (routeProps.path) {
      handleRouteLoad(routeProps.path);
    }
  }, []);

  return (
    <R
      {...rest}
      render={({ location: untypeLoaction, ...props }) => (
        <RenderRoute
          location={location as H.Location<ILocationState>}
          {...routeProps}
          {...props}
        />
      )}
    />
  );
});

const RenderRoute: React.ReactFCWithChildren<RouteComponentProps & IRouteProps> = props => {
  const [ match, setMatch ] = React.useState(props.match);
  const [ computedMatch, setComputedMatch ] = React.useState(props.match);
  const { pageComponent: PageComponent } = props;

  const { error, user } = useMappedState(({ global }: IStore) => ({
    error: global.error,
    user: global.user
  }));

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (props.match.params !== match.params) {
      setMatch(props.match);
    }
  }, [
    match,
    props.match,
    props.match.params
  ]);

  React.useEffect(() => {
    if (props.match.params !== match) {
      setComputedMatch(computedMatch);
    }
  }, [
    computedMatch,
    match,
    props.match.params
  ]);

  React.useEffect(() => {
    if (error && process.env.REACT_APP_ENVIRONMENT !== "dev") {
      Sentry.captureException(error, user ? {
        user: {
          id: user.id.toString(),
          email: user.email
        }
      } : {});
    }
  }, [ error ]);

  // render errors
  if (error) {
    switch (error.status) {
      case 403:
        return <NoAccess {...props} />;

      case 404:
        return <NotFound {...props} />;

      case 503:
        if (error.type === "api_maintenance_error") {
          // Clear the error now that we know that we need to take the user
          // to the maintenance screen
          dispatch(setGlobalError(null));

          return (
            <Redirect
              to={{
                pathname: "/maintenance",
                state: { from: props.location }
              }}
            />
          );
        }

        return <GenericError {...props} />;

      default:
        return <GenericError {...props} />;
    }
  }

  if (!props.privateRoute || props.authenticated) {
    return (
      <PageWrapper
        className="page-wrapper"
        halign="center"
      >
        {PageComponent && (
          <PageComponent
            {...props}
            match={match}
            computedMatch={computedMatch}
          />
        )}
      </PageWrapper>
    );
  } else if (!props.hasPermission) {
    return <NoAccess {...props} />;
  } else {
    return (
      <Redirect
        to={{
          pathname: props.redirect,
          state: { from: props.location }
        }}
      />
    );
  }
};

export default Route;

const PageWrapper = styled(Grid.Bounds).attrs({
  direction: "vertical"
  // padding: "20px"
})`
  width: 100%;
  height: 100%;
  /* position: fixed;
  top: 110px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 100px);
  overflow-y: scroll; */
`;
